import {
  Box,
  Typography,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Avatar,
  Button,
  AlertTitle,
  IconButton,
  CircularProgress,
  Switch,
  Badge,
  Chip,
  Paper,
  useMediaQuery,
  useTheme,
  Drawer,
  Fab,
} from "@mui/material";
import Alert from "@mui/material/Alert";
import CloseIcon from "@mui/icons-material/Close";
import PeopleAltIcon from "@mui/icons-material/PeopleAlt";
import NotificationsActiveIcon from "@mui/icons-material/NotificationsActive";
import ChatBubbleOutlineIcon from "@mui/icons-material/ChatBubbleOutline";
import useBroadcastLive from "./useBroadcastLive";
import UsersPanel from "./UsersPanel";
import { styles, getInitials } from "./broadcastLive.styles";

const BroadcastLive = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isTablet = useMediaQuery(theme.breakpoints.between("sm", "md"));

  const {
    parentRef,
    comments,
    loader,
    isSend,
    setIsSend,
    onlineUsers,
    hideComments,
    usersDrawerOpen,
    setUsersDrawerOpen,
    user,
    sendNotification,
    manageLiveChat,
  } = useBroadcastLive();

  return (
    <Box sx={styles.container}>
      {/* Header */}
      <Paper elevation={0} sx={styles.header}>
        <Box sx={styles.headerLeft}>
          <Box sx={styles.liveDot} />
          <Typography
            variant={isMobile ? "subtitle1" : "h6"}
            fontWeight={600}
            color="text.primary"
          >
            En Vivo
          </Typography>

          <Chip
            icon={<PeopleAltIcon sx={{ fontSize: 16 }} />}
            label={`${onlineUsers.length} conectadas`}
            size="small"
            variant="outlined"
            sx={{
              ...styles.connectedChip(theme),
              cursor: isMobile ? "pointer" : "default",
            }}
            onClick={isMobile ? () => setUsersDrawerOpen(true) : undefined}
          />

          {user?.admin_users?.LIVES?.WRITE?.[0] && (
            <Box sx={styles.chatToggle}>
              <Typography variant="caption" color="text.secondary">
                {!hideComments ? "Chat activo" : "Chat pausado"}
              </Typography>
              <Switch
                checked={hideComments}
                onChange={manageLiveChat}
                size="small"
                color="primary"
              />
            </Box>
          )}
        </Box>

        <Button
          variant="contained"
          onClick={sendNotification}
          disabled={loader}
          startIcon={!loader ? <NotificationsActiveIcon /> : undefined}
          sx={styles.notifyButton(theme)}
        >
          {!loader ? (
            "Notificar en Vivo!"
          ) : (
            <CircularProgress size={22} color="inherit" />
          )}
        </Button>
      </Paper>

      {/* Content */}
      <Box sx={styles.content}>
        {/* Comments Panel */}
        <Paper elevation={0} sx={styles.commentsPanel}>
          <Box sx={styles.commentsHeader}>
            <ChatBubbleOutlineIcon
              sx={{ fontSize: 18, color: "text.secondary" }}
            />
            <Typography variant="subtitle2" color="text.secondary">
              Comentarios
            </Typography>
            <Chip
              label={comments.length}
              size="small"
              sx={styles.commentCountChip}
            />
          </Box>

          <List ref={parentRef} sx={styles.commentsList}>
            {comments.length === 0 ? (
              <Box sx={styles.emptyState}>
                <ChatBubbleOutlineIcon sx={{ fontSize: 48 }} />
                <Typography variant="body2">
                  Esperando comentarios...
                </Typography>
              </Box>
            ) : (
              comments.map((comment, index) => (
                <ListItem key={index} sx={styles.commentItem(index)}>
                  <ListItemAvatar sx={{ minWidth: 40, mt: 0.5 }}>
                    <Avatar sx={styles.commentAvatar(comment?.name)}>
                      {getInitials(comment?.name, "")}
                    </Avatar>
                  </ListItemAvatar>
                  <ListItemText
                    primary={
                      <Typography
                        variant="caption"
                        fontWeight={700}
                        color="primary"
                        component="span"
                      >
                        {comment?.name}
                      </Typography>
                    }
                    secondary={
                      <Typography
                        variant="body2"
                        color="text.primary"
                        sx={{ wordBreak: "break-word", lineHeight: 1.4 }}
                      >
                        {comment?.message}
                      </Typography>
                    }
                    sx={{ my: 0 }}
                  />
                </ListItem>
              ))
            )}
          </List>
        </Paper>

        {/* Users Panel - Desktop/Tablet */}
        {!isMobile && (
          <Paper elevation={0} sx={styles.usersPanel(isTablet)}>
            <UsersPanel onlineUsers={onlineUsers} />
          </Paper>
        )}
      </Box>

      {/* Mobile: FAB to open users drawer */}
      {isMobile && (
        <Fab
          size="medium"
          color="primary"
          onClick={() => setUsersDrawerOpen(true)}
          sx={styles.fab}
        >
          <Badge badgeContent={onlineUsers.length} color="error" max={99}>
            <PeopleAltIcon />
          </Badge>
        </Fab>
      )}

      {/* Mobile: Users Drawer */}
      <Drawer
        anchor="right"
        open={usersDrawerOpen}
        onClose={() => setUsersDrawerOpen(false)}
      >
        <UsersPanel onlineUsers={onlineUsers} />
      </Drawer>

      {/* Success Alert */}
      {isSend && (
        <Alert
          severity="success"
          sx={styles.successAlert}
          action={
            <IconButton
              aria-label="close"
              color="inherit"
              size="small"
              onClick={() => setIsSend(false)}
            >
              <CloseIcon fontSize="inherit" />
            </IconButton>
          }
        >
          <AlertTitle>Notificacion enviada</AlertTitle>
          Se ha notificado a todas las sirenas.
        </Alert>
      )}
    </Box>
  );
};

export default BroadcastLive;
